import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, Minus, Plus, Ruler, Star, ShieldCheck, Truck, RefreshCw } from "lucide-react";
import api, { inr } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { SizeChartModal } from "@/components/SizeChartModal";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [active, setActive] = useState(0);
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  const [rev, setRev] = useState({ name: "", rating: 5, comment: "" });
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();

  useEffect(() => {
    window.scrollTo(0, 0);
    setSize("");
    api.get(`/products/${id}`).then(({ data }) => {
      setP(data);
      document.title = `${data.name} — KKashigari India`;
    });
    api.get(`/products/${id}/related`).then(({ data }) => setRelated(data));
    api.get(`/products/${id}/reviews`).then(({ data }) => setReviews(data));
  }, [id]);

  if (!p) return <div className="py-40 text-center uplabel text-[var(--olive)]">Loading…</div>;

  const discount = p.mrp > p.price ? Math.round(((p.mrp - p.price) / p.mrp) * 100) : 0;

  const onAdd = () => {
    if (!size) return toast.error("Please select a size");
    addItem(p, size, qty);
    toast.success(`${p.name} added to bag`);
  };

  const onWish = async () => {
    const res = await toggle(p.id);
    if (res.needAuth) toast.error("Please sign in to save favourites");
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!rev.name || !rev.comment) return toast.error("Please fill all fields");
    const { data } = await api.post(`/products/${id}/reviews`, rev);
    setReviews([data, ...reviews]);
    setRev({ name: "", rating: 5, comment: "" });
    toast.success("Thank you for your review");
  };

  return (
    <div data-testid="product-detail-page" className="max-w-[1400px] mx-auto px-5 lg:px-10 py-10 lg:py-14">
      <div className="text-xs text-[var(--olive)] mb-8 flex gap-2">
        <Link to="/" className="hover:text-[var(--bronze)]">Home</Link> /
        <Link to={`/shop?audience=${p.audience}`} className="hover:text-[var(--bronze)] capitalize">{p.audience}</Link> /
        <span className="text-[var(--bronze)]">{p.name}</span>
      </div>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        {/* Gallery */}
        <div className="flex gap-4">
          <div className="flex flex-col gap-3 w-20 shrink-0">
            {p.images.map((img, i) => (
              <button key={i} onClick={() => setActive(i)} data-testid={`thumb-${i}`}
                className={`aspect-[3/4] overflow-hidden border ${active === i ? "border-[var(--olive)]" : "border-transparent"}`}>
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
          <div className="flex-1 aspect-[3/4] overflow-hidden bg-[#F3EECB]">
            <img src={p.images[active]} alt={p.name} className="w-full h-full object-cover" />
          </div>
        </div>

        {/* Info */}
        <div>
          <div className="uplabel mb-3">{p.audience} · {p.category.replace("-", " ")} · {p.occasion}</div>
          <h1 className="font-serif text-4xl lg:text-5xl text-[var(--bronze)] leading-tight">{p.name}</h1>

          <div className="flex items-center gap-2 mt-4">
            <div className="flex">{[1, 2, 3, 4, 5].map((s) => <Star key={s} className={`w-4 h-4 ${s <= Math.round(p.rating) ? "fill-[var(--olive)] text-[var(--olive)]" : "text-[var(--line)]"}`} />)}</div>
            <span className="text-sm text-[var(--olive)]">{p.rating} · {p.reviews_count} reviews</span>
          </div>

          <div className="flex items-center gap-3 mt-6">
            <span className="text-3xl font-serif text-[var(--bronze)]">{inr(p.price)}</span>
            {discount > 0 && <><span className="text-lg text-[var(--olive)]/70 line-through">{inr(p.mrp)}</span><span className="uplabel text-[var(--maroon)]">{discount}% Off</span></>}
          </div>
          <p className="text-xs text-[var(--olive)] mt-1">Inclusive of all taxes</p>

          <p className="text-[var(--bronze)] mt-6 leading-relaxed">{p.description}</p>

          {/* Size */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <span className="uplabel">Select Size</span>
              <SizeChartModal audience={p.audience} trigger={
                <button className="flex items-center gap-1.5 text-xs text-[var(--olive)] hover:text-[var(--bronze)] transition-colors"><Ruler className="w-3.5 h-3.5" /> Size Guide</button>
              } />
            </div>
            <div className="flex flex-wrap gap-2.5">
              {p.sizes.map((s) => (
                <button key={s} onClick={() => setSize(s)} data-testid={`size-${s}`}
                  className={`min-w-[3.5rem] px-3 py-3 text-sm border transition-colors ${size === s ? "bg-[var(--bronze)] text-[#FBF7DE] border-[var(--bronze)]" : "border-[var(--line)] text-[var(--bronze)] hover:border-[var(--olive)]"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Qty + Add */}
          <div className="flex items-stretch gap-3 mt-8">
            <div className="flex items-center border border-[var(--line)]">
              <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus" className="px-4 text-[var(--bronze)]"><Minus className="w-4 h-4" /></button>
              <span className="w-10 text-center text-[var(--bronze)]" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} data-testid="qty-plus" className="px-4 text-[var(--bronze)]"><Plus className="w-4 h-4" /></button>
            </div>
            <button onClick={onAdd} data-testid="add-to-cart-btn" className="btn-primary flex-1 uplabel py-4">Add To Bag</button>
            <button onClick={onWish} data-testid="pdp-wishlist-btn" className="w-14 grid place-items-center border border-[var(--line)] hover:border-[var(--olive)] transition-colors">
              <Heart className={`w-5 h-5 ${has(p.id) ? "fill-[var(--maroon)] text-[var(--maroon)]" : "text-[var(--bronze)]"}`} />
            </button>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-8 text-center">
            {[[ShieldCheck, "Secure Pay"], [RefreshCw, "7-Day Returns"], [Truck, "COD Available"]].map(([Icon, l]) => (
              <div key={l} className="border border-[var(--line)] py-4 flex flex-col items-center gap-1.5">
                <Icon className="w-5 h-5 text-[var(--olive)]" strokeWidth={1.3} />
                <span className="text-xs text-[var(--bronze)]">{l}</span>
              </div>
            ))}
          </div>

          {/* Details */}
          <Accordion type="single" collapsible className="mt-8" defaultValue="fabric">
            <AccordionItem value="fabric" className="border-[var(--line)]">
              <AccordionTrigger className="uplabel text-[var(--bronze)]">Fabric & Details</AccordionTrigger>
              <AccordionContent className="text-[var(--bronze)] leading-relaxed">
                <p><strong className="text-[var(--olive)]">Fabric:</strong> {p.fabric}</p>
                <p><strong className="text-[var(--olive)]">Colour:</strong> {p.color}</p>
                <p><strong className="text-[var(--olive)]">Occasion:</strong> {p.occasion}</p>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="care" className="border-[var(--line)]">
              <AccordionTrigger className="uplabel text-[var(--bronze)]">Care Instructions</AccordionTrigger>
              <AccordionContent className="text-[var(--bronze)] leading-relaxed">{p.care}</AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-24">
        <h2 className="font-serif text-3xl text-[var(--bronze)] mb-8">Customer Reviews</h2>
        <div className="grid lg:grid-cols-3 gap-12">
          <form onSubmit={submitReview} data-testid="review-form" className="space-y-4 lg:col-span-1">
            <input value={rev.name} onChange={(e) => setRev({ ...rev, name: e.target.value })} placeholder="Your name" data-testid="review-name"
              className="w-full bg-transparent border-b border-[var(--line)] py-2 outline-none text-[var(--bronze)] placeholder:text-[var(--olive)]/60" />
            <div className="flex gap-1.5">{[1, 2, 3, 4, 5].map((s) => (
              <button type="button" key={s} onClick={() => setRev({ ...rev, rating: s })} data-testid={`review-star-${s}`}>
                <Star className={`w-6 h-6 ${s <= rev.rating ? "fill-[var(--olive)] text-[var(--olive)]" : "text-[var(--line)]"}`} />
              </button>
            ))}</div>
            <textarea value={rev.comment} onChange={(e) => setRev({ ...rev, comment: e.target.value })} placeholder="Share your experience" rows={4} data-testid="review-comment"
              className="w-full bg-transparent border border-[var(--line)] p-3 outline-none text-[var(--bronze)] placeholder:text-[var(--olive)]/60" />
            <button type="submit" data-testid="review-submit" className="btn-primary uplabel px-6 py-3">Submit Review</button>
          </form>
          <div className="lg:col-span-2 space-y-6">
            {reviews.length === 0 && <p className="text-[var(--olive)]">Be the first to review this piece.</p>}
            {reviews.map((r) => (
              <div key={r.id} className="border-b border-[var(--line)] pb-6">
                <div className="flex items-center gap-2">
                  <div className="flex">{[1, 2, 3, 4, 5].map((s) => <Star key={s} className={`w-3.5 h-3.5 ${s <= r.rating ? "fill-[var(--olive)] text-[var(--olive)]" : "text-[var(--line)]"}`} />)}</div>
                  <span className="font-serif text-lg text-[var(--bronze)]">{r.name}</span>
                </div>
                <p className="text-[var(--bronze)] mt-2">{r.comment}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-24">
          <h2 className="font-serif text-3xl text-[var(--bronze)] mb-8">You May Also Like</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8">
            {related.map((rp, i) => <ProductCard key={rp.id} product={rp} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
